// 右側屬性面板：選取主題時編輯標題、圖示、顏色、外框、收合、備註；選取關聯線時編輯標籤或刪除。
import { ICON_NAMES, COLOR_NAMES, THEMES, drawIcon, getTheme, type Topic } from '@sind/core';
import type { EditorState } from './state.js';
import type { Commands } from './commands.js';
import { findTopic } from './commands.js';
import { t } from './i18n.js';

// 建立元素的小工具。
function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { className?: string } = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const element = Object.assign(document.createElement(tag), props);
  element.append(...children);
  return element;
}

export class Inspector {
  private readonly body: HTMLElement;
  private renderedFor = '';

  constructor(private readonly root: HTMLElement, private readonly state: EditorState, private readonly commands: Commands) {
    this.body = el('div', { className: 'inspector-body' });
    root.append(this.body);
  }

  // 依選取狀態重建面板；正在輸入的欄位不重建，避免游標跳走。
  refresh(): void {
    const selection = this.state.selection;
    const active = document.activeElement;
    const typing = active instanceof HTMLElement && this.body.contains(active) && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') && (active as HTMLInputElement).type !== 'color';
    const key = selection?.kind === 'topic' ? `t:${selection.id}` : selection?.kind === 'relation' ? `r:${selection.from}>${selection.to}` : '';
    if (typing && key === this.renderedFor) return;
    this.renderedFor = key;
    this.body.replaceChildren();
    if (selection?.kind === 'topic') {
      const topic = findTopic(this.state.map.root, selection.id);
      if (topic) this.renderTopic(topic);
    } else if (selection?.kind === 'relation') {
      this.renderRelation(selection.from, selection.to);
    } else {
      this.body.append(el('p', { className: 'muted', textContent: t('panel.empty') }));
    }
  }

  private section(title: string, ...content: Node[]): HTMLElement {
    return el('section', { className: 'panel-section' }, [el('h3', { textContent: title }), ...content]);
  }

  private renderTopic(topic: Topic): void {
    const isRoot = topic.id === this.state.map.root.id;
    const id = topic.id;

    const title = el('input', { type: 'text', value: topic.title, className: 'field' });
    title.addEventListener('change', () => { const value = title.value.trim(); if (value) this.commands.rename(id, value); else title.value = topic.title; });
    title.addEventListener('keydown', event => { if (event.key === 'Enter') title.blur(); });

    // 圖示格：用 core 的手繪圖示預覽。
    const swatch = getTheme('sketch').swatches.gray!;
    const icons = el('div', { className: 'icon-grid' });
    const none = el('button', { type: 'button', className: `icon-cell none${topic.icon ? '' : ' active'}`, title: t('panel.none'), textContent: '∅' });
    none.addEventListener('click', () => this.commands.setAttrs(id, { icon: null }));
    icons.append(none);
    for (const name of ICON_NAMES) {
      const button = el('button', { type: 'button', className: `icon-cell${topic.icon === name ? ' active' : ''}`, title: name });
      button.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22">${drawIcon(name, 0, 0, 24, swatch, `picker:${name}`)}</svg>`;
      button.addEventListener('click', () => this.commands.setAttrs(id, { icon: name }));
      icons.append(button);
    }

    // 顏色：自動、調色盤、自訂。
    const colors = el('div', { className: 'color-row' });
    const auto = el('button', { type: 'button', className: `color-cell auto${topic.color ? '' : ' active'}`, title: t('panel.none') });
    auto.addEventListener('click', () => this.commands.setAttrs(id, { color: null }));
    colors.append(auto);
    for (const name of COLOR_NAMES) {
      const button = el('button', { type: 'button', className: `color-cell${topic.color === name ? ' active' : ''}`, title: name });
      button.style.background = THEMES.sketch!.swatches[name]!.stroke;
      button.addEventListener('click', () => this.commands.setAttrs(id, { color: name }));
      colors.append(button);
    }
    const custom = el('input', { type: 'color', className: 'color-custom', title: t('panel.custom'), value: topic.color?.startsWith('#') ? topic.color : '#6965db' });
    custom.addEventListener('change', () => this.commands.setAttrs(id, { color: custom.value }));
    colors.append(custom);

    const sections: HTMLElement[] = [
      this.section(t('panel.title'), title),
      this.section(t('panel.icon'), icons),
      this.section(t('panel.color'), colors),
    ];

    if (!isRoot) {
      const hasBoundary = topic.boundary !== undefined;
      const toggle = el('input', { type: 'checkbox', checked: hasBoundary });
      toggle.addEventListener('change', () => this.commands.setAttrs(id, { boundary: toggle.checked ? '' : null }));
      const label = el('input', { type: 'text', className: 'field', value: topic.boundary ?? '', placeholder: t('panel.boundaryLabel'), disabled: !hasBoundary });
      label.addEventListener('change', () => this.commands.setAttrs(id, { boundary: label.value.trim() }));
      sections.push(this.section(t('panel.boundary'), el('label', { className: 'check' }, [toggle, t('panel.boundary')]), label));
    }

    if (topic.children.length) {
      const collapse = el('input', { type: 'checkbox', checked: Boolean(topic.collapsed) });
      collapse.addEventListener('change', () => this.commands.setAttrs(id, { collapsed: collapse.checked }));
      sections.push(el('section', { className: 'panel-section' }, [el('label', { className: 'check' }, [collapse, t('panel.collapsed')])]));
    }

    const note = el('textarea', { className: 'field note', value: topic.note ?? '', rows: 5 });
    note.addEventListener('change', () => this.commands.setAttrs(id, { note: note.value.trim() || null }));
    note.id = 'panel-note';
    sections.push(this.section(t('panel.note'), note));

    this.body.append(el('h2', { textContent: t('panel.topic') }), ...sections);
  }

  private renderRelation(from: string, to: string): void {
    const relation = this.state.map.relations.find(item => item.from === from && item.to === to);
    if (!relation) return;
    const label = el('input', { type: 'text', className: 'field', value: relation.label ?? '' });
    label.addEventListener('change', () => this.commands.setRelationLabel(from, to, label.value.trim()));
    label.addEventListener('keydown', event => { if (event.key === 'Enter') label.blur(); });
    const remove = el('button', { type: 'button', className: 'button danger', textContent: t('panel.delete') });
    remove.addEventListener('click', () => this.commands.deleteSelection());
    this.body.append(el('h2', { textContent: t('panel.relation') }), this.section(t('panel.relationLabel'), label), remove);
  }

  focusNote(): void {
    this.root.querySelector<HTMLTextAreaElement>('#panel-note')?.focus();
  }
}
